import { useCallback, useEffect, useRef } from 'react';

/**
 * Wrap a callback so it runs at most once per mount. The latch is consumed by the
 * first call, so callers guarding on external readiness (data loaded, context
 * resolved) must guard before calling, not inside the callback.
 *
 * The callback is kept in a ref so the returned function has a stable identity
 * and never runs a stale closure.
 */
export function useOnceCallback<Args extends unknown[]>(callback: (...args: Args) => void): (...args: Args) => void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const hasCalledRef = useRef(false);

  return useCallback((...args: Args) => {
    if (hasCalledRef.current) {
      return;
    }

    hasCalledRef.current = true;
    callbackRef.current(...args);
  }, []);
}

/**
 * Run a callback exactly once per mount, the first time `shouldRun` is true.
 * Made for effects on components whose data refetches while they stay mounted
 * (a new object identity per refetch must not re-run the callback), and for
 * work that must wait for data or context to arrive (pass `shouldRun: false`
 * until ready).
 */
export function useOnceWhen(shouldRun: boolean, callback: () => void) {
  const runOnce = useOnceCallback(callback);

  useEffect(() => {
    if (shouldRun) {
      runOnce();
    }
  }, [shouldRun, runOnce]);
}
